import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../App";
import PortalEmailVerificationPanel, { formatResendCooldown } from "./PortalEmailVerificationPanel";
import {
  PORTAL_LIFECYCLE_PHASE,
  mapEmailVerificationSendError,
} from "../../utils/portalEmailVerificationModel";
import {
  classifyPortalEmailVerificationRolloutFromUser,
  shouldShowPortalEmailVerificationPanel,
} from "../../utils/portalEmailVerificationRolloutPolicy";
import { sendPortalVerificationEmail } from "../../services/sendPortalVerificationEmailCallable";
import { readVerificationResendCooldownAt } from "../../utils/portalEmailVerificationSendGuard";
import { sendPortalVerificationEmailGuarded } from "../../utils/clientPortalInviteLifecycleController";
import { clearPortalAuthSessionState, parseInviteCodeFromLocationSearch } from "../../utils/portalAuthErrors";

export default function PortalWorkspaceVerificationGate({ user, onVerified = () => {} }) {
  const location = useLocation();
  const inviteCode = useMemo(
    () => parseInviteCodeFromLocationSearch(location.search),
    [location.search]
  );
  const [lifecyclePhase, setLifecyclePhase] = useState(PORTAL_LIFECYCLE_PHASE.VERIFICATION_PENDING);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [verificationLastSentAt, setVerificationLastSentAt] = useState(
    () => readVerificationResendCooldownAt() || 0
  );

  const rollout = useMemo(
    () => classifyPortalEmailVerificationRolloutFromUser(user),
    [user]
  );

  useEffect(() => {
    if (!user || user.isAnonymous) return;
    if (user.emailVerified === true) {
      onVerified();
      return;
    }
    if (rollout.clientErrorMessage) {
      setError(rollout.clientErrorMessage);
    }
    setLifecyclePhase(PORTAL_LIFECYCLE_PHASE.VERIFICATION_PENDING);
  }, [user, rollout.clientErrorMessage, onVerified]);

  const sendVerificationEmailOnce = useCallback(async () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const sendResult = await sendPortalVerificationEmailGuarded({
      sendFn: async () => {
        await sendPortalVerificationEmail({
          continueUrl: origin,
          inviteCode,
        });
      },
    });
    if (!sendResult.sent) {
      if (sendResult.error) throw sendResult.error;
      return sendResult;
    }
    setVerificationLastSentAt(sendResult.sentAt || readVerificationResendCooldownAt());
    setLifecyclePhase(PORTAL_LIFECYCLE_PHASE.VERIFICATION_EMAIL_SENT);
    return sendResult;
  }, [inviteCode]);

  const handleResendVerificationEmail = async () => {
    if (busy || !auth.currentUser) return;
    setBusy(true);
    setError("");
    try {
      const sendResult = await sendVerificationEmailOnce();
      if (sendResult?.sent) {
        setNotice("Verification email sent. Check your inbox.");
      }
    } catch (sendErr) {
      setLifecyclePhase(PORTAL_LIFECYCLE_PHASE.VERIFICATION_DELIVERY_FAILED);
      setError(mapEmailVerificationSendError(sendErr));
    } finally {
      setBusy(false);
    }
  };

  const handleVerifiedEmailContinue = async () => {
    if (busy || !auth.currentUser) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await auth.currentUser.reload();
      const refreshed = auth.currentUser;
      if (refreshed.emailVerified !== true) {
        setLifecyclePhase(PORTAL_LIFECYCLE_PHASE.VERIFICATION_PENDING);
        setNotice("Your email is not verified yet. Open the link in your inbox, then try again.");
        return;
      }
      await refreshed.getIdToken(true);
      onVerified();
    } catch (verifyErr) {
      console.error("Workspace verification check failed:", verifyErr);
      setError("We could not confirm your email verification. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      clearPortalAuthSessionState();
    } catch (signOutErr) {
      console.error("Portal workspace verification sign out:", signOutErr);
    }
  };

  if (rollout.clientErrorMessage && !shouldShowPortalEmailVerificationPanel({ user, lifecyclePhase })) {
    return (
      <div className="mx-auto flex max-w-7xl flex-1 items-center justify-center px-4 py-16 md:px-6">
        <div className="max-w-lg rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-md">
          <p className="text-sm text-slate-700">{rollout.clientErrorMessage}</p>
          <button
            type="button"
            className="mt-6 text-sm font-medium text-slate-700 underline decoration-slate-400 underline-offset-[3px] hover:text-slate-900"
            onClick={handleSignOut}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (!shouldShowPortalEmailVerificationPanel({ user, lifecyclePhase })) {
    return null;
  }

  return (
    <PortalEmailVerificationPanel
      email={user?.email || ""}
      lifecyclePhase={lifecyclePhase}
      busy={busy}
      resendCooldownMs={formatResendCooldown(verificationLastSentAt)}
      error={error}
      notice={notice}
      onOpenEmail={() => {
        const domain = String(user?.email || "")
          .split("@")[1]
          ?.trim();
        if (domain) {
          window.open(`https://${domain}`, "_blank", "noopener,noreferrer");
        }
      }}
      onResend={handleResendVerificationEmail}
      onVerified={handleVerifiedEmailContinue}
      onSignOut={handleSignOut}
    />
  );
}
